/**
 * Owner: Haselton Baker Risk Group, LLC
 * Copyright All Rights Reserved
 */
// @flow
import type { Element } from 'react';

import numeral from 'numeral';
import React, { useEffect, useState } from 'react';
import { Field } from 'redux-form';
import { Row, Col } from 'reactstrap';
import SubmitButton from '#components/support/buttons/SubmitButton/index.jsx';
import { FieldFeedback } from '#components/support/forms/utility/index.js';
import RenderUncontrolledInput from '#components/support/forms/RenderUncontrolledInput/index.jsx';
import Tip from '#components/support/tooltips/Tip/index.jsx';
import SampleUploadLink from './SampleUploadLink/index.jsx';
import {
  controls,
  input,
  button,
  wrapper,
  uploadGuideTip,
} from './StructuralResponseUploader.module.scss';

type Props = {|
  accept: string,
  edpKey: string,
  form: string,
  handleUpload: Function,
  isUploading: boolean | null,
  maxBytes: number,
  method: string,
  name: string,
  value: string | null,
|};

const getUploadGuideTipId = method => {
  if (method === 'rha') {
    return 'sampleUploadGuideRha';
  }

  if (method === 'medians') {
    return 'sampleUploadGuideMedians';
  }

  return null;
};

const StructuralResponseUploader = (props: Props): Element<typeof Row> => {
  const { accept, edpKey, form, handleUpload, isUploading, maxBytes, method, name, value } = props;

  const [showSpinner, setShowSpinner] = useState(false);
  const [preUploadError, setPreUploadError] = useState(null);
  const uploadGuideTipId = getUploadGuideTipId(method);

  useEffect(() => {
    setShowSpinner(false);
  }, [method, edpKey]);

  useEffect(() => {
    if (value && isUploading !== null) {
      setShowSpinner(isUploading);
    }
  }, [isUploading, value]);

  // TODO: figure out proper flow type for e
  const handleChange = (e: any) => {
    e.preventDefault();
    setPreUploadError(null);
    const newFile = e.target.files[0];
    if (newFile) {
      const { size } = newFile;
      if (size > maxBytes) {
        setPreUploadError(`File exceeds the maximum ${numeral(maxBytes).format('0b')}`);
      } else {
        setShowSpinner(true);
        handleUpload(newFile);
        e.target.value = ''; // clear target so you can upload the same file
      }
    }
  };
  return (
    <Row>
      <Col>
        <div id="structuralResponseUploader" className="position-relative p-2">
          <div className={controls}>
            <div className={wrapper}>
              <Field
                name={name}
                id={name}
                type="file"
                component={RenderUncontrolledInput}
                accept={accept}
                onChange={handleChange}
                inputClassName={input}
              />
              <SubmitButton
                submittingText="Uploading"
                submitting={showSpinner}
                type="button"
                className={button}
              >
                Upload
              </SubmitButton>
            </div>
            {uploadGuideTipId && (
              <span className={uploadGuideTip}>
                <Tip tipId={uploadGuideTipId} />
              </span>
            )}
          </div>
          {preUploadError && (
            <div id={`${name}-feedback`} className="text-danger">
              {preUploadError}
            </div>
          )}
          <FieldFeedback form={form} name={name} />
        </div>
      </Col>
      <Col>
        <div className="float-right">
          <SampleUploadLink edpKey={edpKey} method={method} />
        </div>
      </Col>
    </Row>
  );
};

export default StructuralResponseUploader;
